/**
 * Cómo se escriben los números en el módulo Estrategias (mapa de diseño 226,
 * decisión 2; spec 232). Una sola función por cosa, así «1.366,40» se lee
 * igual en Hoy, Por decidir, Productos, Análisis y el historial. Siempre es-BO:
 * `es` a secas no separa los miles de 4 cifras («3250»).
 */

const LOCAL = 'es-BO';
/** El signo menos de verdad (U+2212), no el guion: se alinea con el «+». */
const MENOS = '−';
/**
 * Lote B2 (spec 246, punto 7): todas las fechas del módulo, en hora de Bolivia,
 * sea cual sea la zona de la computadora (como el backend, `BUSINESS_TIME_ZONE`).
 */
const ZONA_DE_BOLIVIA = 'America/La_Paz';
const ANIO_EN_BOLIVIA = new Intl.DateTimeFormat('en-US', {
  timeZone: ZONA_DE_BOLIVIA,
  year: 'numeric',
});
/** El año de un instante en Bolivia (el 31 de diciembre a las 22:00 sigue siendo ese año). */
const anioEnBolivia = (d: Date) => Number(ANIO_EN_BOLIVIA.format(d));

/** 3250 → «3.250,00», 3518.9 → «3.518,90». Siempre dos decimales. */
export const formatoPrecio = (n: number) =>
  n.toLocaleString(LOCAL, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Igual que `formatoPrecio` pero en OTRA convención numérica, a propósito: el Pricer Datec escribe como su
 * documento (en-US: 14121.511 → «14,121.51»). Vive acá, con los demás formateadores, para que haya un solo
 * lugar que escribe montos.
 */
export const formatoPrecioEn = (n: number, local: string) =>
  n.toLocaleString(local, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** 1688 → «1.688». Para contar productos, precios o veces: sin decimales. */
export const formatoCantidad = (n: number) =>
  Math.round(n).toLocaleString(LOCAL, { maximumFractionDigits: 0 });

/**
 * 12.5 → «12,5», 18 → «18», 1.25 → «1,25»: un número sin ceros de más (el %
 * de una regla o de un umbral, «pregunta si el cambio supera 12,5 %»).
 */
export const formatoNumero = (n: number, maxDecimales = 2) =>
  n.toLocaleString(LOCAL, { maximumFractionDigits: maxDecimales });

/**
 * Con los decimales FIJOS: 0.0096 → «0,010» con 3, 38 → «38,0» con 1. Para medidas
 * que se leen en columna (volumen y peso por unidad en Comercia), donde un «38»
 * al lado de un «2,1» se lee desalineado.
 */
export const formatoFijo = (n: number, decimales: number) =>
  n.toLocaleString(LOCAL, { minimumFractionDigits: decimales, maximumFractionDigits: decimales });

/** «1 producto», «1.688 productos». */
export const cantidadDe = (n: number, uno: string, varios: string) =>
  `${formatoCantidad(n)} ${n === 1 ? uno : varios}`;

/**
 * 20.73 → «20,7 %»; con `signo`, «−20,7 %» o «+22,7 %» (el cero va sin signo).
 * Una decimal salvo que se pida otra cosa (`decimales: 0` → «−13 %»).
 *
 * 7B-5: `decimalesMaximos` escribe hasta N decimales SIN forzar ceros
 * (mínimo 0). Es el formato de los umbrales configurados: un umbral 7,5 se
 * lee «7,5 %» y uno 8 «8 %» — con `decimales: 0` el 7,5 quedaba «8 %».
 */
export function formatoPorcentaje(
  n: number,
  {
    signo = false,
    decimales = 1,
    decimalesMaximos,
    local = LOCAL,
  }: { signo?: boolean; decimales?: number; decimalesMaximos?: number; local?: string } = {}
): string {
  if (decimalesMaximos != null) {
    const redondo = Number(n.toFixed(decimalesMaximos));
    const cuerpo = Math.abs(redondo).toLocaleString(local, {
      maximumFractionDigits: decimalesMaximos,
    });
    if (!signo) return `${redondo < 0 ? MENOS : ''}${cuerpo} %`;
    return `${redondo > 0 ? '+' : redondo < 0 ? MENOS : ''}${cuerpo} %`;
  }
  const redondo = Number(n.toFixed(decimales));
  const cuerpo = Math.abs(redondo).toLocaleString(local, {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
  if (!signo) return `${redondo < 0 ? MENOS : ''}${cuerpo} %`;
  return `${redondo > 0 ? '+' : redondo < 0 ? MENOS : ''}${cuerpo} %`;
}

/**
 * 7C-5: el formato de un umbral/corte configurado o medido: hasta una decimal
 * sin forzar ceros (7,5 → «7,5 %», 8 → «8 %»). La regla tiene nombre: un
 * umbral escrito con `decimales: 0` redondea 7,5 a «8 %» y lo que la empresa
 * configuró no se lee. El guardián de porcentajes marca esa forma.
 */
export const formatoUmbral = (n: number) => formatoPorcentaje(n, { decimalesMaximos: 1 });

/** Una diferencia de precio con su signo: «−47,48», «+1.366,40», «0,00». */
export function formatoDiferencia(n: number): string {
  const redondo = Math.round(n * 100) / 100;
  const cuerpo = formatoPrecio(Math.abs(redondo));
  return `${redondo > 0 ? '+' : redondo < 0 ? MENOS : ''}${cuerpo}`;
}

/** «00:57», «06:00»: la hora en 24 h, nunca «12:57 a. m.». En hora de Bolivia (lote B2). */
export function formatoHora(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString(LOCAL, {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: ZONA_DE_BOLIVIA,
  });
}

/**
 * «12 sept»: el día y el mes corto; de otro año, con el año («1 jun 2025»).
 * Agregados del 29-sep (A1): «creada el …» en Estrategias. Lote B2: el día y el
 * año, los de Bolivia (un cambio a las 22:00 no se va al día siguiente).
 */
export function formatoFecha(iso: string | Date, hoy: Date = new Date()): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  const dia = d
    .toLocaleDateString(LOCAL, { day: 'numeric', month: 'short', timeZone: ZONA_DE_BOLIVIA })
    .replace('.', '');
  const anio = anioEnBolivia(d);
  return anio === anioEnBolivia(hoy) ? dia : `${dia} ${anio}`;
}

/** «25 sept, 00:57»: el día, el mes corto y la hora en 24 h; de otro año, con el año. En hora de Bolivia. */
export function formatoFechaYHora(iso: string | Date, hoy: Date = new Date()): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return `${formatoFecha(d, hoy)}, ${formatoHora(d)}`;
}

/**
 * «hace 20 min», «hace 3 h», «hace 2 días». Sin fecha dice que todavía no
 * corrió: inventar un «hace un momento» ahí sería mentir sobre la vigilancia.
 * Vive acá (y no en la pantalla que lo usa) porque lo leen varias: Hoy,
 * Productos y el paso 4 (ronda final, C4).
 */
export function haceCuanto(iso: string | null, ahora: number = Date.now()): string {
  if (!iso) return 'todavía sin correr';

  const min = Math.max(0, Math.round((ahora - new Date(iso).getTime()) / 60000));
  if (min < 1) return 'hace un momento';
  if (min < 60) return `hace ${min} min`;

  const h = Math.round(min / 60);
  if (h < 24) return `hace ${h} h`;

  const d = Math.round(h / 24);
  return d === 1 ? 'hace 1 día' : `hace ${d} días`;
}

/**
 * Lote B1 (spec 246 puntos 2 y 3; D10): una fecha en HORA DE BOLIVIA, siempre
 * con el año: «3 oct 2026, 10:42», o «31 ago 2026» sin la hora. Aparte de
 * `formatoFecha` (que usa la hora de la computadora y pone el año sólo si no es
 * el de hoy) porque el lote B2 pasa todo el módulo a Bolivia (punto 7) y ahí
 * se juntan. La pantalla dice «hora de Bolivia» una sola vez (D10).
 */
const HORA_24 = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' } as const;
const DIA_MES_ANIO = { day: 'numeric', month: 'short', year: 'numeric' } as const;
const FECHA_BOLIVIA = new Intl.DateTimeFormat(LOCAL, {
  timeZone: ZONA_DE_BOLIVIA,
  ...DIA_MES_ANIO,
});
const FECHA_Y_HORA_BOLIVIA = new Intl.DateTimeFormat(LOCAL, {
  timeZone: ZONA_DE_BOLIVIA,
  ...DIA_MES_ANIO,
  ...HORA_24,
});

export function formatoFechaBolivia(
  iso: string | Date,
  { hora = false }: { hora?: boolean } = {}
): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  const p = Object.fromEntries(
    (hora ? FECHA_Y_HORA_BOLIVIA : FECHA_BOLIVIA).formatToParts(d).map((x) => [x.type, x.value])
  );
  const fecha = `${p.day} ${p.month.replace('.', '')} ${p.year}`;
  return hora ? `${fecha}, ${p.hour}:${p.minute}` : fecha;
}
